// import AuthHeader from "@/components/AuthHeader";
import { useEffect, useMemo, useState } from "react";
import { Eye, EyeOff, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { setPassword as setPasswordApi } from "../api/auth";
import { useSearchParams, useNavigate } from "react-router-dom";
import { isGuestUser, getGuestExamUser } from "../utils/storage";
import Footer from "@/components/Footer"

interface SetPasswordFormData {
  password: string;
  confirmPassword: string;
}

const SetPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isGuestUpgrade, setIsGuestUpgrade] = useState(false);
  const [capturedToken, setCapturedToken] = useState<string | null>(null);

  // CRITICAL: Capture tokens immediately on mount before any Supabase client initialization
  useEffect(() => {
    // Capture token immediately from URL before Supabase clears it
    const hash = window.location.hash?.replace(/^#/, "");
    const search = window.location.search;

    console.log("🔍 Full URL:", window.location.href);
    console.log("🔍 Hash:", hash);
    console.log("🔍 Search:", search);

    let token = null;
    let tokenFromHash = false;

    if (hash) {
      const params = new URLSearchParams(hash);
      token = params.get("access_token") || params.get("token");
      if (token) tokenFromHash = true;
      console.log("🚨 URGENT: Captured token from hash on mount:", token ? token.substring(0, 20) + "..." : "none");

      // Debug: Check what parameters are in the hash
      console.log("🔍 Hash parameters:", Object.fromEntries(params.entries()));

      // Check for expired token errors
      if (params.get('error_code') === 'otp_expired' || params.get('error') === 'access_denied') {
        console.log("🚨 Detected expired/invalid link, redirecting to forgot-password");
        navigate('/forgot-password', { state: { error: 'Your password link has expired or is no longer valid. Please enter your email below to get a new link' } });
        return;
      }
    }

    if (!token && search) {
      const params = new URLSearchParams(search);
      token = params.get("access_token") || params.get("token");
      console.log("🚨 URGENT: Captured token from query on mount:", token ? token.substring(0, 20) + "..." : "none");

      // Debug: Check what parameters are in the query
      console.log("🔍 Query parameters:", Object.fromEntries(params.entries()));

      // Also check search params for errors (fallback)
      if (params.get('error_code') === 'otp_expired' || params.get('error') === 'access_denied') {
        console.log("🚨 Detected expired/invalid link in query params, redirecting to forgot-password");
        navigate('/forgot-password', { state: { error: 'Link expired, please enter email to resend' } });
        return;
      }
    }

    if (token) {
      setCapturedToken(token);
      console.log("✅ Token successfully captured and stored in state");

      // Move token from hash to query params to keep it visible in URL
      if (tokenFromHash) {
        const newUrl = `${window.location.pathname}?access_token=${token}`;
        window.history.replaceState(null, '', newUrl);
        console.log("🔄 Moved token to query params:", newUrl);
      }

      // Debug: Decode the token to see what type it is
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        console.log("🔍 Token payload:", {
          sub: payload.sub,
          role: payload.role,
          aud: payload.aud,
          is_anonymous: payload.is_anonymous,
          iss: payload.iss
        });
      } catch (e) {
        console.error("❌ Failed to decode token:", e);
      }
    } else {
      console.log("❌ No token found in URL");
    }
  }, []);

  // CRITICAL: Capture tokens immediately before any Supabase client initialization
  // This prevents Supabase from clearing the URL parameters
  const tokenFromHash = useMemo(() => {
    // Capture from URL immediately
    const hash = window.location.hash?.replace(/^#/, "");
    if (!hash) return undefined;
    const params = new URLSearchParams(hash);
    const token = params.get("access_token") || params.get("token");
    console.log("🔍 Captured token from hash:", token ? token.substring(0, 20) + "..." : "none");
    return token;
  }, []);
  
  const tokenFromQuery = useMemo(() => {
    // Capture from query params immediately
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("access_token") || urlParams.get("token");
    console.log("🔍 Captured token from query:", token ? token.substring(0, 20) + "..." : "none");
    return token;
  }, []);
  // Check if this is a guest upgrade immediately
  const upgradeParam = searchParams.get("upgrade");
  const guestStatus = isGuestUser();
  const isGuestUpgradeImmediate = upgradeParam === "true" || guestStatus;
  
  // Set state for UI purposes
  useEffect(() => {
    setIsGuestUpgrade(isGuestUpgradeImmediate);
  }, [isGuestUpgradeImmediate]);

  // Get the appropriate token - for guest users, use guest token
  const guestData = getGuestExamUser();
  const guestToken = guestData?.token;
  
  // CRITICAL: For email links, always use the URL token (it's a real user token)
  // For guest upgrades, use the URL token if available, otherwise fall back to guest token
  const accessToken = capturedToken || tokenFromHash || tokenFromQuery || 
    (isGuestUpgradeImmediate ? guestToken : localStorage.getItem("access_token")) || 
    guestToken;

  // If we have a URL token (from email link), it's a real user token - use it!
  if (capturedToken || tokenFromHash || tokenFromQuery) {
    console.log("🎯 Using email link token (real user token) instead of anonymous token");
    
    // Clear anonymous session data since we're now using a real user token
    if (isGuestUpgradeImmediate) {
      console.log("🧹 Clearing anonymous session data...");
      localStorage.removeItem("guestExamUser");
      localStorage.removeItem("session_id");
      localStorage.removeItem("sb-nluntjftnyzlxhatvvks-auth-token");
    }
  }

  // Debug logging
  console.log("Token sources:", {
    capturedToken: capturedToken ? capturedToken.substring(0, 20) + "..." : "none",
    tokenFromHash: tokenFromHash ? tokenFromHash.substring(0, 20) + "..." : "none",
    tokenFromQuery: tokenFromQuery ? tokenFromQuery.substring(0, 20) + "..." : "none",
    guestToken: guestToken ? guestToken.substring(0, 20) + "..." : "none",
    localStorageToken: localStorage.getItem("access_token") ? localStorage.getItem("access_token")?.substring(0, 20) + "..." : "none",
    selectedToken: accessToken ? accessToken.substring(0, 20) + "..." : "none"
  });

  const form = useForm<SetPasswordFormData>({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const password = form.watch("password");

  const onSubmit = async (data: SetPasswordFormData) => {
    setIsLoading(true);
    setError("");
    setSuccess("");
    try {
      const token = accessToken;
      console.log("Setting password with token:", token ? "present" : "missing");
      console.log("Token type:", token ? (token.includes("guest") ? "guest token" : "regular token") : "none");
      console.log("Is guest upgrade:", isGuestUpgradeImmediate);
      console.log("Guest data:", guestData);
      console.log("Selected token:", token === guestToken ? "guest token" : "other token");
      
      const res = await setPasswordApi(data.password, token);
      console.log("Password set successfully:", res);
      setSuccess(res.message || "Password updated successfully");
      
      // Store the new user token if we used an email link token
      if (capturedToken || tokenFromHash || tokenFromQuery) {
        console.log("💾 Storing new user token in localStorage...");
        localStorage.setItem("access_token", accessToken);
        localStorage.setItem("sb-nluntjftnyzlxhatvvks-auth-token", JSON.stringify({
          access_token: accessToken,
          token_type: "bearer",
          expires_in: 3600,
          user: { id: "new-user-id" } // This will be updated by Supabase
        }));
      }
      
      // Clear guest data if this was a guest upgrade
      if (isGuestUpgradeImmediate) {
        console.log("Clearing guest data...");
        localStorage.removeItem("guestExamUser");
        localStorage.removeItem("session_id");
      }
      
      setTimeout(() => {
        if (isGuestUpgradeImmediate) {
          console.log("Redirecting to exam results...");
          navigate("/exam-results"); // Redirect to exam results to see full results
        } else {
          console.log("Redirecting to login...");
          navigate("/login");
        }
      }, 1200);
    } catch (err: any) {
      console.error("Set password error:", err);
      console.error("Error details:", err?.response?.data);
      setError(err?.response?.data?.error || err?.message || "Failed to set password");
    } finally {
      setIsLoading(false);
    }
  };

 return (
  <div className="login-container">
    
    <div className="flex items-center justify-center w-full px-4 pt-12 pb-24 md:pt-16 md:pb-32">
      <Card className="w-full max-w-md p-8 rounded-2xl shadow-sm">
        
        <CardHeader className="text-center space-y-1">
          {isGuestUpgradeImmediate && (
            <div className="flex justify-center mb-4">
              <Crown className="h-12 w-12 text-yellow-500" />
            </div>
          )}

          <CardTitle className="text-2xl font-bold">
            {isGuestUpgradeImmediate
              ? "Complete Your Premium Setup"
              : "Set your password to begin"}
          </CardTitle>

          <CardDescription>
            {isGuestUpgradeImmediate
              ? "Create a password to access your premium account and unlock your full exam results"
              : "Create a password to finish activating your account"}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mt-6">
              {error && <div className="text-sm text-red-600">{error}</div>}
              {success && <div className="text-sm text-green-600">{success}</div>}

              {/* Password Field */}
              <FormField
                control={form.control}
                name="password"
                rules={{
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters",
                  },
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).+$/,
                    message:
                      "Password must contain an uppercase letter, a lowercase letter, a number, and a symbol",
                  },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="!text-foreground">Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          {...field}
                          className={`h-11 px-4 text-base border border-gray-200 focus-visible:ring-2 focus-visible:ring-blue-500 ${
                            form.formState.errors.password ? "border-red-500 focus-visible:ring-red-500" : ""
                          }`}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Confirm Password Field */}
              <FormField
                control={form.control}
                name="confirmPassword"
                rules={{
                  required: "Please confirm your password",
                  validate: (value) =>
                    value === password || "Passwords do not match",
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="!text-foreground">Confirm Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Re-enter your password"
                            {...field}
                            className={`h-11 px-4 text-base border border-gray-200 focus-visible:ring-2 focus-visible:ring-blue-500 ${
                              form.formState.errors.confirmPassword ? "border-red-500 focus-visible:ring-red-500" : ""
                            }`}
                          />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

                   <Button
                      type="submit"
                      disabled={isLoading}
                      className="
                        w-full h-11
                        text-base font-medium rounded-lg
                        mt-6
                        bg-[#005FCC] hover:bg-[#0049A8]
                        text-white
                        transition-colors
                        disabled:opacity-50
                      "
                    >
                      {isLoading ? "Setting Password..." : "Set Password"}
                 </Button>
              </form>
            </Form>
          </CardContent>
  
        </Card>
      </div>
  
      <div className="footer-container-login">
        <Footer />
      </div>
    </div>
  );
};

export default SetPassword;
